import React, { useState, useEffect, useCallback } from "react";
import { useImpressionJobStore } from "../../store/useImpressionJobStore";
import { useAppStore } from "../../store/useAppStore";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import type { ImpressionJob } from "../../types/impressionJob";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { ImpressionJobsTable } from "./ImpressionJobsTable";
import { ImpressionJobFilters } from "./ImpressionJobFilters";
import { ImpressionJobPagination } from "./ImpressionJobPagination";
import { RefreshCw } from "lucide-react";

export const ImpressionJobsList: React.FC = () => {
  const setSelectedJobId = useAppStore((state) => state.setSelectedJobId);
  const navigateToView = useAppNavigate();
  const {
    jobs,
    loading,
    totalCount,
    currentPage,
    totalPages,
    itemsPerPage,
    stats,
    fetchJobs,
    fetchStats,
    deleteJob,
  } = useImpressionJobStore();

  // Local state for filters
  const [campaignFilter, setCampaignFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "processing" | "completed" | "failed">("");
  const [cityFilter, setCityFilter] = useState("");

  // Fetch stats on mount
  useEffect(() => {
    fetchStats().catch(console.error);
  }, [fetchStats]);

  // Fetch jobs with current filters and page
  const fetchJobsWithFilters = useCallback(
    (page: number) => {
      const filters: any = {};

      if (campaignFilter) {
        filters.campaign_id = campaignFilter;
      }
      if (statusFilter) {
        filters.status = statusFilter;
      }
      if (cityFilter) {
        filters.city = cityFilter;
      }

      fetchJobs(filters, page, itemsPerPage);
    },
    [campaignFilter, statusFilter, cityFilter, itemsPerPage, fetchJobs]
  );

  // Fetch jobs on mount and when filters change
  useEffect(() => {
    fetchJobsWithFilters(1);
  }, [campaignFilter, statusFilter, cityFilter, fetchJobsWithFilters]);

  const handlePageChange = (page: number) => {
    fetchJobsWithFilters(page);
  };

  const handleRefresh = () => {
    fetchJobsWithFilters(currentPage);
    fetchStats().catch(console.error);
  };

  const handleViewDetails = (job: ImpressionJob) => {
    setSelectedJobId(job.id);
    navigateToView("impression-job-detail");
  };

  const handleDelete = async (jobId: number) => {
    if (window.confirm("Are you sure you want to delete this impression job?")) {
      try {
        await deleteJob(jobId);
      } catch (error) {
        console.error("Error deleting job:", error);
      }
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Impression Jobs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage and view campaign impression analytics
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">
              Total Jobs
            </div>
            <div className="mt-1 text-3xl font-semibold text-gray-900">
              {stats.total}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">
              Processing
            </div>
            <div className="mt-1 text-3xl font-semibold text-yellow-600">
              {stats.by_status.processing}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">
              Completed
            </div>
            <div className="mt-1 text-3xl font-semibold text-green-600">
              {stats.by_status.completed}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500">
              Failed
            </div>
            <div className="mt-1 text-3xl font-semibold text-red-600">
              {stats.by_status.failed}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <ImpressionJobFilters
        campaignFilter={campaignFilter}
        statusFilter={statusFilter}
        cityFilter={cityFilter}
        onCampaignFilterChange={setCampaignFilter}
        onStatusFilterChange={setStatusFilter}
        onCityFilterChange={setCityFilter}
      />

      {/* Jobs Table */}
      <ImpressionJobsTable
        jobs={jobs}
        loading={loading}
        onViewDetails={handleViewDetails}
        onDelete={handleDelete}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <ImpressionJobPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
